import { ChronoUnit, DateTimeFormatter, Instant, ZoneId } from '@js-joda/core'
import { Locale } from '@js-joda/locale_en'
import { isEmptyString, isValidNumberString } from './string-utils'
import { twoDPRounder } from './rounding-formatting-utils'

/**
 * Parses a UTC number or number string and formats it according to ISO 8601 date time without offset
 * Example: 2024-04-24T02:02:33Z
 * @param value UTC time string
 */
export function formatToUTCTimestampFromUTCNumber (value: string | number): string {
  const invalidString = typeof value === 'string' && !isValidNumberString(value)
  const invalidNumber = typeof value === 'number' && Number.isNaN(value)
  if (invalidString || invalidNumber) {
    return ''
  }

  try {
    const numberValue = (typeof value === 'string') ? Number(value) : value
    const formattedTime = Instant.ofEpochMilli(numberValue).atZone(ZoneId.systemDefault())
    return formattedTime.format(DateTimeFormatter.ISO_INSTANT).toString()
  } catch (exception) {
    console.error(`Error occurred when parsing time string: ${value}`)
    return ''
  }
}

/**
 * Example: 2024-04-24 11:04:00.100 AM
 */
const MILLISECOND_DATE_TIME_FORMATTER = DateTimeFormatter
  .ofPattern('yyyy-MM-dd hh:mm:ss.SSS a')
  .withLocale(Locale.ENGLISH)

/**
 * Parses a UTC string and formats it according to MILLISECOND_DATE_TIME_FORMATTER
 * @param value UTC time number or number string
 */
export function formatToLocalMilliTimestampFromUTCString (value: string): string {
  if (isEmptyString(value)) {
    return ''
  } else {
    try {
      const formattedTime = Instant.parse(value).atZone(ZoneId.systemDefault())
      return formattedTime.format(MILLISECOND_DATE_TIME_FORMATTER).toString()
    } catch (exception) {
      console.error(`Error occurred when parsing time string: ${value}`)
      return ''
    }
  }
}

/**
 * Parses a UTC number or number string and formats it according to MILLISECOND_DATE_TIME_FORMATTER
 * @param value UTC time string
 */
export function formatToLocalMilliTimestampFromUTCNumber (value: string | number): string {
  const invalidString = typeof value === 'string' && !isValidNumberString(value)
  const invalidNumber = typeof value === 'number' && Number.isNaN(value)
  if (invalidString || invalidNumber) {
    return ''
  }

  try {
    const numberValue = (typeof value === 'string') ? Number(value) : value
    const formattedTime = Instant.ofEpochMilli(numberValue).atZone(ZoneId.systemDefault())
    return formattedTime.format(MILLISECOND_DATE_TIME_FORMATTER).toString()
  } catch (exception) {
    console.error(`Error occurred when parsing time string: ${value}`)
    return ''
  }
}

/**
 * Parses a UTC number or number string and formats it according to the provided pattern
 * @param value UTC time string
 * @param pattern date time formatter pattern
 */
export function formatToLocalPatternFromUTCNumber (value: string | number, pattern: string): string {
  const invalidString = typeof value === 'string' && !isValidNumberString(value)
  const invalidNumber = typeof value === 'number' && Number.isNaN(value)
  if (invalidString || invalidNumber) {
    return ''
  }

  try {
    const numberValue = (typeof value === 'string') ? Number(value) : value
    const formattedTime = Instant.ofEpochMilli(numberValue).atZone(ZoneId.systemDefault())
    const dateTimeFormatter = DateTimeFormatter.ofPattern(pattern).withLocale(Locale.ENGLISH)
    return formattedTime.format(dateTimeFormatter).toString()
  } catch (exception) {
    console.error(`Error occurred when parsing time string: ${value}`)
    return ''
  }
}

/**
 * Example: 2024-04-24 11:04:00.100100100 AM
 */
const NANOSECOND_DATE_TIME_FORMATTER = DateTimeFormatter
  .ofPattern('yyyy-MM-dd hh:mm:ss.nnnnnnnnn a')
  .withLocale(Locale.ENGLISH)

/**
 * Parses a UTC string and formats it according to NANOSECOND_DATE_TIME_FORMATTER
 * @param value UTC time string
 */
export function formatToLocalNanoTimestampFromUTCString (value: string): string {
  if (isEmptyString(value)) {
    return ''
  } else {
    try {
      const formattedTime = Instant.parse(value).atZone(ZoneId.systemDefault())
      return formattedTime.format(NANOSECOND_DATE_TIME_FORMATTER).toString()
    } catch (exception) {
      console.error(`Error occurred when parsing time string: ${value}`)
      return ''
    }
  }
}

function formatTimeDifference (timeDifference: number): string {
  let delta = Math.abs(timeDifference) / 1000
  const days = Math.floor(delta / 86400)
  delta -= days * 86400
  const hours = Math.floor(delta / 3600) % 24
  delta -= hours * 3600
  const minutes = Math.floor(delta / 60) % 60
  delta -= minutes * 60
  const seconds = twoDPRounder(delta % 60) // in theory the modulus is not required

  return `${days !== 0 ? String(days) + 'd ' : ''}${hours !== 0 ? String(hours) + 'h ' : ''}${minutes !== 0 ? String(minutes) + 'm ' : ''}${seconds}s`
}

export function formatTimeDifferenceBetweenThenAndNow (then: string): string {
  const fromTime = Instant.parse(then).atZone(ZoneId.systemDefault())
  const toTime = Instant.ofEpochMilli(Date.now()).atZone(ZoneId.systemDefault())
  return formatTimeDifference(fromTime.until(toTime, ChronoUnit.MILLIS))
}

export function formatTimeDifferenceBetweenTwoInstants (from: string, to: string): string {
  const fromTime = Instant.parse(from).atZone(ZoneId.systemDefault())
  const toTime = Instant.parse(to).atZone(ZoneId.systemDefault())
  return formatTimeDifference(fromTime.until(toTime, ChronoUnit.MILLIS))
}
